import * as React from 'react';
import { IconButton } from '@mui/material';
import AddCircleIcon from '@material-ui/icons/AddCircleRounded';
import Remove from '@material-ui/icons/Remove';
import { PhoneNumberField } from './PhoneNumberField';
import { COUNTRY_PHONE_PREFIXES } from './CountryPhonePrefixField';

export interface MultiplePhoneNumberFieldProps {
    id?: string;
    label?: string;
    placeholder?: string;
    values: string[];
    disabled?: boolean;
    variant?: 'standard' | 'outlined' | 'filled' | 'nobox';
    style?: React.CSSProperties;
    onChange: (values: string[]) => void;
}

interface PhoneState {
    prefix: string;
    number: string;
}

const knownPrefixes = COUNTRY_PHONE_PREFIXES.map(c => c.prefix);
export const splitPhoneCodeFromNumber = (v: string): {prefix: string, number: string} => {

    const trimmed = (v ?? '').trim();

    if (trimmed.startsWith('+')) {
        const match = knownPrefixes
            .filter(p => trimmed.startsWith(p))
            .sort((a, b) => b.length - a.length)[0];

        if (match) {
            const rest = trimmed.substring(match.length).trimStart();
            return { prefix: match, number: rest };
        }

        return { prefix: '+351', number: trimmed.replace(/^\+/, '') };
    }

    return { prefix: '+351', number: trimmed };
}

export const MultiplePhoneNumberField = (props: MultiplePhoneNumberFieldProps) => {
    const { id, label, placeholder, values, style, variant, onChange, disabled } = props;

    // Initialize state: parse incoming values ONCE
    const [phones, setPhones] = React.useState<PhoneState[]>(() => values.map(splitPhoneCodeFromNumber));

    // Track last values emitted to parent to avoid feedback loops on prop sync
    const lastEmittedRef = React.useRef<string[] | null>(null);

    // Update callback: combine and send to parent
    const updateParent = React.useCallback((newPhones: PhoneState[]) => {
        const formatted = newPhones.map(p => `${p.prefix}${p.number}`);
        lastEmittedRef.current = formatted;
        onChange(formatted);
    }, [onChange]);

    // Sync internal state if external values prop changes (e.g., after API load)
    React.useEffect(() => {
        const incoming = (values ?? []).map(v => (v ?? '').trim());
        const last = lastEmittedRef.current ?? [];
        const sameLength = incoming.length === last.length;
        const isSame = sameLength && incoming.every((v, i) => v === last[i]);
        if (isSame) return; // ignore updates we just emitted

        const knownPrefixes = COUNTRY_PHONE_PREFIXES.map(c => c.prefix);
        const parsed: PhoneState[] = incoming.map(trimmed => {
            if (trimmed.startsWith('+')) {
                const match = knownPrefixes
                    .filter(p => trimmed.startsWith(p))
                    .sort((a, b) => b.length - a.length)[0];

                if (match) {
                    const rest = trimmed.substring(match.length).trimStart();
                    return { prefix: match, number: rest };
                }

                return { prefix: '+351', number: trimmed.replace(/^\+/, '') };
            }

            return { prefix: '+351', number: trimmed };
        });
        setPhones(parsed);
    }, [values]);

    const onPhoneChanged = (number: string, prefix: string, idx: number) => {
        const newPhones = [...phones];
        newPhones[idx] = { prefix, number };
        setPhones(newPhones);
        updateParent(newPhones);
    };

    const onRemove = (idx: number) => {
        const newPhones = phones.filter((_, i) => i !== idx);
        setPhones(newPhones);
        updateParent(newPhones);
    };

    const onAdd = () => {
        const newPhones = [...phones, { prefix: '+351', number: '' }];
        setPhones(newPhones);
        updateParent(newPhones);
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 'var(--spacer-XS)', ...style}}>
            {label &&
                <label style={{ fontSize: '0.8rem', marginBottom: '8px', display: 'block', fontWeight: 500, height: 0, top: '-15px', position: 'relative' }}>
                    {label}
                </label>
            }
            {(phones.length === 0 ? [{prefix: '+351', number: ''}] : phones).map((phone, idx) => (
                <div key={idx} style={{ display: 'flex'}}>
                    <PhoneNumberField
                        id={id ? `${id}-${idx}` : undefined}
                        value={phone.number}
                        prefix={phone.prefix}
                        placeholder={placeholder}
                        disabled={disabled}
                        variant={variant}
                        onChange={(number, prefix) => onPhoneChanged(number, prefix, idx)}
                    />
                    {!disabled && (
                        <>
                            <IconButton
                                id={id ? `${id}-${idx}-remove-button` : undefined}
                                onClick={() => onRemove(idx)}
                                size="small"
                                style={{ padding: 'var(--spacer-XXS)' }}
                            >
                                <Remove fontSize="small" />
                            </IconButton>
                            {idx === phones.length - 1 && (
                                <IconButton
                                    id={id ? `${id}-add-button` : undefined}
                                    onClick={onAdd}
                                    size="small"
                                    style={{ padding: 'var(--spacer-XXS)' }}
                                >
                                    <AddCircleIcon fontSize="small" />
                                </IconButton>
                            )}
                        </>
                    )}
                </div>
            ))}
        </div>
    );
}
